import ErrorOutline from "@mui/icons-material/ErrorOutline";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, BoxProps, Stack, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import CopyableTypography from "components/Typography/CopyableTypography";
import React, { useRef } from "react";

import { MiniModal, ThemeColor, useMiniModal } from "./MiniModal";
import { TruncatedText } from "./TruncatedText";
import { useExpandWhenClipped } from "./useExpandWhenClipped";

// 14px when using the small prop, 16px by default
const labelFontSize = "0.75rem";
// labels are not rendered when using the small prop
const getValueFontSize = (small: boolean) => (small ? "0.875rem" : "1rem");

export function DisplayOnlyField({
  text,
  subtext,
  label,
  small = false,
  renderValue,
  copyable = true,
  infoTooltip,
  labelSpacing = 0,
  wrapValue = false,
  warningIcon = false,
  warningTooltip,
  groupingKey = false,
  groupingKeyTooltip,
  showCopyHint = false,
  expandOnClick = false,
  expandBackgroundColor,
  copyLabel,
  tabIndex = 0,
}: {
  text?: string;
  subtext?: string | undefined | null;
  label?: React.ReactNode;
  small?: boolean;
  renderValue?: (value: React.ReactNode) => React.ReactNode;
  copyable?: boolean;
  infoTooltip?: React.ReactNode;
  /** Spacing between label and value in MUI spacing units. Defaults to 0. */
  labelSpacing?: number;
  /** When true, the value text wraps instead of truncating with ellipsis. Defaults to false. */
  wrapValue?: boolean;
  /** When true, shows a warning icon beside the label. */
  warningIcon?: boolean;
  /** Tooltip shown on the warning icon (e.g. an explanation of the warning). */
  warningTooltip?: React.ReactNode;
  /** When true, shows an info icon beside the label to mark this as the single
   * field whose value identified and grouped this record. */
  groupingKey?: boolean;
  /** Tooltip shown on the grouping-key icon (e.g. what the grouping means). */
  groupingKeyTooltip?: React.ReactNode;
  /**
   * Shows the "Click to copy" hint on hover. Off by default: most fields here
   * sit in dense read-only panels where a hint on every value is noise. Turn it
   * on where the copy affordance is the point, so the field matches a
   * copyable cell elsewhere on the same screen.
   */
  showCopyHint?: boolean;
  /**
   * When the value is cut off by its ellipsis, a click opens it in full in a
   * popover, copyable per `copyable`. A value that fits behaves as if this
   * were off. Off by default.
   */
  expandOnClick?: boolean;
  /** Background of the expanded popover; pass the surface the field sits on. */
  expandBackgroundColor?: ThemeColor;
  /** Names the value for screen readers ("Copy EPA ID …"). Defaults to `label` when it is a string. */
  copyLabel?: string;
  /** Inside a DataGrid cell, the cell's `tabIndex`, so rows don't each add a Tab stop. */
  tabIndex?: 0 | -1;
}) {
  const valueFontSize = getValueFontSize(small);
  const rootRef = useRef<HTMLDivElement>(null);
  const { modalOpen, openModal, closeModal } = useMiniModal(expandOnClick);
  const { valueRef, isClipped, onClickCapture, onKeyDown } = useExpandWhenClipped({
    enabled: expandOnClick,
    text,
    onOpen: openModal,
  });

  // Native title shows the value on hover unless the copy hint already does; a
  // clipped value is also a button that opens it in full by click or keyboard.
  const title = showCopyHint ? undefined : text;
  const valueBoxProps: BoxProps = isClipped
    ? {
        title,
        role: "button",
        tabIndex,
        "aria-haspopup": "dialog",
        "aria-expanded": modalOpen,
        onKeyDown,
        // Doubled `&` outranks TruncatedText's own `cursor: default` whatever the style order.
        sx: { cursor: "pointer", "&& *": { cursor: "pointer" } },
      }
    : { title };

  const copyableText = text ? (
    !copyable ? (
      <TruncatedText
        ref={valueRef}
        text={text}
        subtext={subtext}
        fontSize={valueFontSize}
        showHoverEffect={false}
      />
    ) : (
      <CopyableFieldValue
        valueRef={valueRef}
        text={text}
        subtext={subtext}
        copyLabel={copyLabelFor(copyLabel, label)}
        tabIndex={tabIndex}
        isClipped={isClipped}
        fontSize={valueFontSize}
        wrapValue={wrapValue}
        showCopyHint={showCopyHint}
      />
    )
  ) : (
    <Typography sx={{ fontSize: valueFontSize, minHeight: valueFontSize }} />
  );

  const value = renderValue ? renderValue(copyableText) : copyableText;

  return (
    <Stack ref={rootRef} spacing={labelSpacing}>
      {(label || infoTooltip || warningIcon || groupingKey) && (
        <Typography
          variant="body2"
          color="text.secondary"
          fontSize={labelFontSize}
          sx={{ display: "flex", alignItems: "center" }}
        >
          {/* The label text truncates; adornment icons keep their size so they
              are never clipped in narrow columns. */}
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Box>
          {warningIcon && (
            <Tooltip title={warningTooltip ?? ""} placement="top">
              <ErrorOutline
                sx={{ fontSize: "1rem", ml: "0.25rem", color: "warning.main", flexShrink: 0 }}
                aria-label="Warning"
              />
            </Tooltip>
          )}
          {groupingKey && (
            <Tooltip title={groupingKeyTooltip ?? ""} placement="top">
              <InfoOutlined
                sx={{ fontSize: "0.875rem", ml: "0.25rem", color: "action.active", flexShrink: 0 }}
                aria-label="Grouped on this field"
              />
            </Tooltip>
          )}
          {infoTooltip && (
            <InfoIconTooltip
              fontSize="1rem"
              iconButtonSx={{
                ml: "0.25rem",
                padding: "1px",
                flexShrink: 0,
              }}
              tooltip={infoTooltip}
            />
          )}
        </Typography>
      )}
      <Box {...valueBoxProps} onClickCapture={onClickCapture}>
        {value}
      </Box>
      <ExpandedValuePopover
        enabled={expandOnClick}
        open={modalOpen}
        onClose={closeModal}
        label={label}
        anchorEl={rootRef.current}
        backgroundColor={expandBackgroundColor}
        text={text}
        subtext={subtext}
        fontSize={valueFontSize}
        copyable={copyable}
        copyLabel={copyLabel}
      />
    </Stack>
  );
}

/** A non-string label (an icon beside it, say) can't name the value for a screen reader. */
function copyLabelFor(copyLabel: string | undefined, label: React.ReactNode) {
  return copyLabel ?? (typeof label === "string" ? label : undefined);
}

function CopyableFieldValue({
  valueRef,
  text,
  subtext,
  copyLabel,
  tabIndex,
  isClipped,
  fontSize,
  wrapValue,
  showCopyHint,
}: {
  valueRef: React.Ref<HTMLDivElement>;
  text: string;
  subtext?: string | null;
  copyLabel?: string;
  tabIndex: 0 | -1;
  isClipped: boolean;
  fontSize: string;
  wrapValue: boolean;
  showCopyHint: boolean;
}) {
  return (
    <CopyableTypography
      ref={valueRef}
      text={text}
      subtext={subtext}
      copyLabel={copyLabel}
      tabIndex={tabIndex}
      // A clipped value is already a button that opens it in full; Enter opens it, not copies.
      focusable={!isClipped}
      sx={{
        fontSize,
        ...(wrapValue
          ? { whiteSpace: "normal", wordBreak: "break-word" }
          : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
      }}
      tooltipOptions={{
        disableHoverMessage: !showCopyHint,
        // The value truncates, so the hint carries it too -- a hover that
        // only advertised the copy would cost the reader the value it copies.
        hoverMessage: showCopyHint ? `${text} — click to copy` : undefined,
      }}
    />
  );
}

/** The full value, wrapped, lifted out of the field in a popover. */
function ExpandedValuePopover({
  enabled,
  open,
  onClose,
  label,
  anchorEl,
  backgroundColor,
  text,
  subtext,
  fontSize,
  copyable,
  copyLabel,
}: {
  enabled: boolean;
  open: boolean;
  onClose: () => void;
  label?: React.ReactNode;
  anchorEl: HTMLElement | null;
  backgroundColor?: ThemeColor;
  text?: string;
  subtext?: string | undefined | null;
  fontSize: string;
  copyable: boolean;
  copyLabel?: string;
}) {
  if (!enabled) {
    return null;
  }
  const wrappedSx = {
    fontSize,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  } satisfies SxProps<Theme>;
  return (
    <MiniModal
      open={open}
      onClose={onClose}
      title={label}
      anchorEl={anchorEl}
      backgroundColor={backgroundColor}
      closeOnMouseLeave
      extendAnchor
    >
      {copyable ? (
        // The pointer is already over the value when this opens, so a hover hint
        // would land on top of the text; only the copied confirmation shows.
        <CopyableTypography
          text={text}
          subtext={subtext}
          copyLabel={copyLabelFor(copyLabel, label)}
          sx={wrappedSx}
          tooltipOptions={{ disableHoverMessage: true }}
        />
      ) : (
        <Box sx={wrappedSx}>
          {text}
          {subtext && (
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {" " + subtext}
            </Typography>
          )}
        </Box>
      )}
    </MiniModal>
  );
}

export function DisplayOnlyFieldList({
  items,
  label,
  small = false,
  copyable = true,
  tabIndex = 0,
}: {
  items: string[];
  label?: string | React.ReactNode;
  small?: boolean;
  copyable?: boolean;
  /** Inside a DataGrid cell, the cell's `tabIndex`, so rows don't each add Tab stops. */
  tabIndex?: 0 | -1;
}) {
  const valueFontSize = getValueFontSize(small);
  return (
    <Stack spacing={0}>
      {label && (
        <Typography variant="body2" color="text.secondary" fontSize={labelFontSize}>
          {label}
        </Typography>
      )}
      <Stack>
        {items.map((item, index) =>
          copyable ? (
            <CopyableTypography
              key={index}
              text={item}
              tabIndex={tabIndex}
              sx={{ fontSize: valueFontSize }}
              tooltipOptions={{
                disableHoverMessage: true,
              }}
            />
          ) : (
            <TruncatedText key={index} text={item} fontSize={valueFontSize} />
          )
        )}
      </Stack>
    </Stack>
  );
}
